import { Injectable } from '@angular/core';
import { CustomerStylingDTO } from 'csr-login-app';
import { lightenDarkenColor } from '@cfp/ui/core';

const DEFAULT_LOGO_WIDTH = 300;
const DEFAULT_LOGO_HEIGHT = 60;

@Injectable({
  providedIn: 'root'
})
export class CustomerStylingService {
  public createStyling(customerStyling: CustomerStylingDTO, stylingLogoUrl: string | undefined): string {
    let styling: string = this.getVars(customerStyling);
    styling += `
        .header-bottom {
            background-color: ${customerStyling.navbarBackgroundColor || customerStyling.primaryColor} !important;
        }

        .header-bottom .end-gradient {
          background: linear-gradient(270deg, ${
            customerStyling.navbarBackgroundColor || customerStyling.primaryColor
          } 25%, rgba(255, 255, 255, 0) 100%) !important;
        }

        .header-bottom .header-bottom-end,
        .header-bottom .header-bottom-end .bottom-buttons {
          background-color: ${customerStyling.navbarBackgroundColor || customerStyling.primaryColor} !important;
        }

        .header-bottom .start-gradient {
          background: linear-gradient(90deg, ${
            customerStyling.navbarBackgroundColor || customerStyling.primaryColor
          } 25%, rgba(255, 255, 255, 0) 100%) !important;
        }

        footer {
            background-color: ${customerStyling.navbarBackgroundColor || customerStyling.primaryColor} !important;
        }

        .header-bottom .nav-item.active .nav-link {
            color: ${customerStyling.primaryColor} !important;
            border-color: ${customerStyling.primaryColor} !important;
        }

        .page-item.active .page-link {
            background-color: ${customerStyling.primaryColor} !important;
            border-color: ${customerStyling.primaryColor} !important;
        }

        .header-bottom .nav-item:hover .nav-link {
            background-color: ${lightenDarkenColor(
              customerStyling.navbarBackgroundColor || customerStyling.primaryColor,
              -20
            )} !important;
        }

        .top-measure-amount {
            color: ${customerStyling.primaryColor} !important;
        }

        .form-check-input:checked {
          background-color: ${customerStyling.primaryColor} !important;
          border-color: ${customerStyling.primaryColor} !important;
        }

        .primary-fill {
            fill: ${customerStyling.primaryColor} !important;
        }

        .primary-stroke {
            stroke: ${customerStyling.primaryColor} !important;
        }

        .icon-square {
            background-color: ${customerStyling.primaryColor}11 !important;
        }

        .icon-square i, .icon-circle i {
            color: ${customerStyling.primaryColor} !important;
        }
        `;

    if (customerStyling.navbarColor) {
      styling += `.header-bottom .nav-item .nav-link, .partner-styling-navbar-color {
                color: ${customerStyling.navbarColor} !important;
            }`;

      styling += `.partner-styling-navbar-color-fill {
                fill: ${customerStyling.navbarColor} !important;
            }`;

      styling += `footer .footer-buttons ul li .btn, footer .footer-bottom, footer .footer-bottom a {
                color: ${customerStyling.navbarColor} !important;
            }`;
    }

    if (stylingLogoUrl) {
      styling += `.header-top .navbar-brand {
                height: ${customerStyling.logoHeight ? customerStyling.logoHeight + 'px' : 'auto'} !important;
                width: ${customerStyling.logoWidth ? customerStyling.logoWidth + 'px' : 'auto'} !important;
                max-height: ${DEFAULT_LOGO_HEIGHT}px !important;
                max-width: ${DEFAULT_LOGO_WIDTH}px !important;
                content: url(${stylingLogoUrl}) !important;
            }

            .header-top .navbar-brand img {
              display: none !important;
            }`;
    }

    if (customerStyling.primaryColor) {
      styling += `.building-report-footer {
              background-color: ${customerStyling.primaryColor} !important;
          }

          .print-cover-page .print-cover-page-title-wrapper {
              --print-cover-page-bg-color: ${customerStyling.primaryColor} !important;
          }`;
    }

    styling += this.getButtonLinkStyling(customerStyling, 'primary');

    const currentStyle = getComputedStyle(document.body);
    const currentPrimaryColor = currentStyle.getPropertyValue('--bs-primary').trim().toLowerCase();
    const isPrimaryColorDifferent = currentPrimaryColor !== customerStyling.primaryColor.toLowerCase();

    let themeColors: string[] = [];
    if (isPrimaryColorDifferent) {
      themeColors.push('primary');
    }
    if (customerStyling.secondaryColor) {
      themeColors.push('secondary');
    }

    themeColors.forEach((themeColorName: string) => {
      const colorName = `${themeColorName}Color`;
      styling += this.getButtonStyling(customerStyling, themeColorName, colorName);
      styling += this.getOutlineButtonStyling(customerStyling, themeColorName, colorName);
      styling += `.text-${themeColorName} {
                color: ${customerStyling[colorName]} !important;
            }`;
    });

    styling += this.getOutlineButtonStyling(customerStyling, 'paging', 'primaryColor', false);
    styling += this.getButtonStyling(customerStyling, 'paging-active', 'primaryColor');

    return styling;
  }

  private getButtonStyling(customerStyling: CustomerStylingDTO, buttonClass: string, colorName: string): string {
    return `.btn-${buttonClass} {
            border-color: ${lightenDarkenColor(customerStyling[colorName], -20)} !important;
            background-color: ${customerStyling[colorName]} !important;
        }

        .btn-${buttonClass}:hover {
            border-color: ${lightenDarkenColor(customerStyling[colorName], -40)} !important;
            background-color: ${lightenDarkenColor(customerStyling[colorName], -20)} !important;
        }`;
  }

  private getOutlineButtonStyling(
    customerStyling: CustomerStylingDTO,
    buttonClass: string,
    colorName: string,
    prefixOutline: boolean = true
  ): string {
    const prefix = prefixOutline ? 'outline-' : '';
    return `.btn-${prefix}${buttonClass} {
            border-color: ${customerStyling[colorName]} !important;
            color: ${customerStyling[colorName]} !important;
        }

        .btn-${prefix}${buttonClass}:hover {
            color: #fff !important;
            border-color: ${customerStyling[colorName]} !important;
            background-color: ${customerStyling[colorName]} !important;
        }

        .btn-group .btn-${prefix}${buttonClass} {
            background-color: ${customerStyling[colorName]}11 !important;
        }

        .btn-group .btn-${prefix}${buttonClass}:hover {
            background-color: ${customerStyling[colorName]} !important;
        }`;
  }

  private getButtonLinkStyling(customerStyling: CustomerStylingDTO, buttonClass: string): string {
    return `.btn-link, .link-anchor {
            color: ${customerStyling.primaryColor} !important;
        }

        .btn-outline-${buttonClass}:hover {
            color: ${lightenDarkenColor(customerStyling.primaryColor, -20)} !important;
        }`;
  }

  private getVars(customerStyling: CustomerStylingDTO): string {
    let styling = `:root {
      --cfp-primary: ${customerStyling.primaryColor} !important;
      --cfp-partner-styling-primary: ${customerStyling.primaryColor} !important;`;
    if (customerStyling.secondaryColor) {
      styling += `
      --cfp-secondary: ${customerStyling.secondaryColor} !important;
      --cfp-partner-styling-secondary: ${customerStyling.secondaryColor} !important;`;
    }
    styling += `--cfp-input-focus-border-color: ${lightenDarkenColor(customerStyling.primaryColor, 200)} !important;
    }

    .progress {
      --bs-progress-bar-bg: ${customerStyling.primaryColor} !important;
    }`;
    return styling;
  }
}
